/**
 * A type guard for objects.
 *
 * @param object - A possible object
 */
export const isObject = (
  object: unknown
): object is Record<string, unknown> => {
  return typeof object === 'object' && object !== null && !Array.isArray(object)
}

/**
 * An object that may not be an `Error`, but shares the same basic shape.
 */
export interface ErrorLike {
  message: string
  name?: string
  stack?: string
}

/**
 * A type guard for error-like objects.
 *
 * @param error - A possible error
 */
export const isErrorLike = (error: unknown): error is ErrorLike =>
  isObject(error) && 'message' in error

/**
 * A type guard for `try...catch` errors.
 *
 * This function is based on:
 * https://github.com/stdlib-js/assert-is-error
 *
 * @param error - A possible error
 */
export const isError = (error: unknown): error is Error => {
  if (!isObject(error)) return false

  // Check for `Error` objects instantiated within the current global context.
  if (error instanceof Error) return true

  return findError(error)
}

/**
 * Walks the prototype tree until an error is found.
 *
 * This handles instances where `Error` objects were instantiated outside of
 * the current global context.
 *
 * @param error - A possible error.
 */
function findError<T extends object>(error: T): boolean {
  if (Object.prototype.toString.call(error) === '[object Error]') {
    return true
  }

  const prototype = Object.getPrototypeOf(error) as T | null

  return prototype === null ? false : findError(prototype)
}

/**
 * Parses errors to string, useful for getting the error message in a
 * `try...catch` statement.
 */
export const errorToString = (error: unknown, fallback?: string): string => {
  if (isError(error) || isErrorLike(error)) return error.message

  if (typeof error === 'string') return error

  return fallback ?? 'An unknown error has occurred.'
}

/**
 * Normalizes unknown errors to the Error type, useful for working with errors
 * in a `try...catch` statement.
 */
export const normalizeError = (error: unknown): Error => {
  if (isError(error)) return error

  const errorMessage = errorToString(error)

  // Copy over additional properties if the object is error-like.
  return isErrorLike(error)
    ? Object.assign(new Error(errorMessage), error)
    : new Error(errorMessage)
}
